import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useAppDispatch, useAppSelector } from "../state/hooks";
import { closeScreenPopup } from "../state/slices/screenPopupSlice";
import { resetScreenFormData } from "../state/slices/formsSlice";
import { executeConfigAction } from "../state/thunks/configThunks";
import { ScreenRenderer } from "./ScreenRenderer";

export const ScreenPopupModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { isOpen, screenId, title } = useAppSelector((s) => s.screenPopup);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleClose = () => {
    if (screenId) {
      dispatch(resetScreenFormData(screenId));
    }
    dispatch(closeScreenPopup());
  };

  useEffect(() => {
    if (!isOpen) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, screenId]);

  if (!isOpen || !screenId) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        // close if click outside the dialog
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
          handleClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-lg bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-end px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <button
            type="button"
            className="px-2 py-1 rounded-md text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:outline-none"
            onClick={handleClose}
            aria-label="Close"
            title="Close"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[calc(90vh-52px)] overflow-auto p-4">
          <ScreenRenderer
            screenId={screenId}
            onActionTrigger={(actionId, data) => dispatch(executeConfigAction(actionId, data))}
            renderMode="popup"
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

